import { sealed } from '@/decorators'
import { BoundForError } from '@/libs/BoundForError'

const REQUEST_TIMEOUT_IN_MS = 10_000

export interface ClientMessage {
  data?: unknown
  requestId?: string
  type: string
}

interface ServerMessage {
  data?: unknown
  requestId?: string
  type: string
}

interface PendingRequest {
  onReject: (error: Error) => void
  onResolve: (data: unknown) => void
  timeoutId: ReturnType<typeof setTimeout>
}

export class WebSocketClientError extends BoundForError {}

@sealed
class WebSocketClient {
  #pendingRequests: Map<string, PendingRequest>
  #socket: WebSocket | undefined

  constructor() {
    this.#pendingRequests = new Map()
  }

  connect(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const endpoint = process.env.BUN_PUBLIC_API_WEBSOCKETS_ENDPOINT
      if (!endpoint) {
        reject(new WebSocketClientError('`BUN_PUBLIC_API_WEBSOCKETS_ENDPOINT` is not defined.'))

        return
      }

      const url = `${endpoint}?token=${encodeURIComponent(token)}`
      let socket: WebSocket
      try {
        socket = new WebSocket(url)
      } catch (error) {
        reject(error instanceof Error ? error : new WebSocketClientError(String(error)))

        return
      }
      this.#socket = socket

      let isSettled = false
      socket.onopen = () => {
        if (!isSettled) {
          isSettled = true
          console.info('[WebSocketClient]', 'Connected to the server!')

          resolve()
        }
      }
      socket.onerror = () => {
        if (!isSettled) {
          isSettled = true

          reject(new WebSocketClientError('Could not connect to the server.'))
        }
      }
      socket.onclose = event => {
        this.#socket = undefined
        this.#rejectAllPendingRequests(new WebSocketClientError('The connection to the server closed.'))
        if (!isSettled) {
          isSettled = true

          reject(new WebSocketClientError(`Connection closed, code=${event.code}.`))
        } else {
          console.info('[WebSocketClient]', `Connection closed, code=${event.code} reason=${event.reason}`)
        }
      }
      socket.onmessage = event => this.#handleMessage(event)
    })
  }

  request<T>(message: ClientMessage): Promise<T> {
    const socket = this.#socket
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return Promise.reject(new WebSocketClientError('Not connected to the server.'))
    }

    const requestId = message.requestId ?? crypto.randomUUID()

    return new Promise<T>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.#pendingRequests.delete(requestId)

        reject(new WebSocketClientError(`Request "${message.type}" timed out.`))
      }, REQUEST_TIMEOUT_IN_MS)
      this.#pendingRequests.set(requestId, {
        onReject: reject,
        onResolve: resolve as (data: unknown) => void,
        timeoutId,
      })

      socket.send(JSON.stringify({ ...message, requestId }))
    })
  }

  #handleMessage(event: MessageEvent) {
    let message: ServerMessage
    try {
      message = JSON.parse(event.data as string) as ServerMessage
    } catch (error) {
      console.warn('[WebSocketClient]', 'Received an unparseable message:', error)

      return
    }

    if (message.requestId) {
      const pending = this.#pendingRequests.get(message.requestId)
      if (pending) {
        clearTimeout(pending.timeoutId)
        this.#pendingRequests.delete(message.requestId)
        if (message.type === 'error') {
          pending.onReject(new WebSocketClientError(readErrorMessage(message.data)))
        } else {
          pending.onResolve(message.data)
        }

        return
      }
    }

    console.info('[WebSocketClient]', 'Unmatched message from server:', message.type)
  }

  #rejectAllPendingRequests(error: Error) {
    for (const pending of this.#pendingRequests.values()) {
      clearTimeout(pending.timeoutId)
      pending.onReject(error)
    }
    this.#pendingRequests.clear()
  }
}

function readErrorMessage(data: unknown): string {
  if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
    return data.message
  }

  return 'The server replied with an unknown error.'
}

export const webSocketClient = new WebSocketClient()
